import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { TreeNode } from 'primeng/api';
import { AlarmService } from '../../services/alarm.service';
import { AuthService } from '../../services/auth.service';
import { DeviceService } from '../../services/device.service';
import { DiagramService } from '../../services/diagram.service';
import { StatsService } from '../../services/stats.service';
import { ButtonIconComponent } from '../../shared/components/button-icon/button-icon.component';
import { ChartMixedComponent } from '../../shared/components/charts/chart-mixed/chart-mixed.component';
import {
  createWaterMeasurementChartData,
  createWaterMeasurementChartOptions,
} from '../../shared/components/charts/measurement-chart.config';
import { TimeRangeSelectorComponent } from '../../shared/components/time-range-selector/time-range-selector.component';
import { TranslateSafePipe } from '../../shared/pipes/translation.pipe';
import { formatTimeAgoTranslation } from '../../shared/utils/date.util';
import { AccountRole } from '../../types/dtos/account.dto';
import { AlarmDto } from '../../types/dtos/alarm.dto';
import {
  DeviceDto,
  DeviceMeasurementDto,
  DistrictMeasurementItemDto,
  DistrictMeasurementResponseDto,
  DistrictMeasurementsDto,
  MeasurementResponseDto,
  WaterUsageTreeRow,
} from '../../types/dtos/device.dto';
import { AlarmSeverity, AlarmStatus } from '../../types/enums/alarm.enum';
import { ReportType } from '../../types/enums/report.enum';
import {
  MixedChartDataSetsInterface,
  MixedChartDateRangeInterface,
  MixedChartInterface,
  MixedChartOptionsInterface,
  TimeRangeKey,
} from '../../types/interfaces/charts.interface';
import { CurrentAlarmComponent } from './current-alarm/current-alarm.component';
import { DashboardBoxComponent } from './dashboard-box/dashboard-box.component';
import { InfoCardComponent } from './info-card/info-card.component';
import { MapComponent } from './map/map.component';
import { AlarmsPieChart } from './pie-chart/alarms-pie-chart';
import { WaterUsagePerDistrict } from './water-usage-per-district/water-usage-per-district';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    InfoCardComponent,
    MapComponent,
    DashboardBoxComponent,
    ChartMixedComponent,
    TimeRangeSelectorComponent,
    CurrentAlarmComponent,
    AlarmsPieChart,
    WaterUsagePerDistrict,
    ButtonIconComponent,
    TranslateSafePipe,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent implements OnInit {
  public devices: DeviceDto[] = [];
  public alarms: AlarmDto[] = [];
  public isFullscreen: boolean = false;
  public waterMeasurementData: MixedChartInterface;
  public waterMeasurementOptions: MixedChartOptionsInterface = createWaterMeasurementChartOptions();
  public waterUsagePerDistrictData: TreeNode<WaterUsageTreeRow>[] = [];
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);
  private _selectedDistrict: string | null = null;
  private _chartFromDate: Date | undefined = undefined;
  private _chartToDate: Date | undefined = undefined;
  private _chartType: TimeRangeKey = '1W';
  private _chartData: DeviceMeasurementDto[] = [];
  private _chartDistrictData: Record<string, DeviceMeasurementDto[]> = {};

  constructor(
    private readonly _deviceService: DeviceService,
    private readonly _alarmService: AlarmService,
    private readonly _diagramService: DiagramService,
    private readonly _router: Router,
    private readonly _translateService: TranslateService,
    private readonly _statsService: StatsService,
    private readonly _authService: AuthService,
  ) {
    this.waterMeasurementData = createWaterMeasurementChartData(this._translateService);
  }

  public get activeDeviceCount(): string {
    let defaultText: string = `${ this._onlineDeviceList() }/<span class="small">${ this.devices.length }`

    let deviceCountYesterday: number | undefined = this.deviceCountYesterday
    if (deviceCountYesterday) {
      let difference: number = this._onlineDeviceList() - deviceCountYesterday
      let deviceCountText: string = this._translateService.instant('common.title.devices_since_yesterday');
      if (difference > 0) {
        defaultText += ` <span class="green">+${ difference } ${ deviceCountText }</span>`;
      } else if (difference < 0) {
        defaultText += ` <span class="red">-${ Math.abs(difference) } ${ deviceCountText }</span>`;
      }
    }

    defaultText += '</span>';

    return defaultText;
  }

  public get deviceCountYesterday(): number | undefined {
    return this._statsService.getActiveDevicesCount() ?? undefined;
  }

  public get activeAlarms(): number {
    return this.alarms.filter((alarm: AlarmDto) =>
      this._isOpenAlarm(alarm),
    ).length;
  }

  public get activeAlarmCountText(): {
    critical: number,
    warning: number,
    high: number,
    info: number
  } {
    const critical: number = this._getAlarmCountBySeverity(AlarmSeverity.CRITICAL);
    const high: number = this._getAlarmCountBySeverity(AlarmSeverity.HIGH);
    const warning: number = this._getAlarmCountBySeverity(AlarmSeverity.WARNING);
    const info: number = this._getAlarmCountBySeverity(AlarmSeverity.INFO);

    return {
      critical,
      high,
      warning,
      info,
    };
  }

  public get lastActiveDeviceDateTime(): string {
    return formatTimeAgoTranslation(this._translateService, this._deviceService.latestMeasurementDateTime);
  }

  public ngOnInit(): void {
    this._deviceService.devices$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((devices: DeviceDto[]) => this.devices = devices);

    this._alarmService.unResolvedAlarms$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (alarms: AlarmDto[]) => this.alarms = alarms,
      });

    this._translateService.onLangChange
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((event: LangChangeEvent) => {
        let dataset: MixedChartDataSetsInterface[] = createWaterMeasurementChartData(this._translateService).datasets

        for (let i = 0; i < dataset.length; i++) {
          this.waterMeasurementData.datasets[i].label = dataset[i].label;
        }
        this.waterMeasurementData = {
          ...this.waterMeasurementData,
        }
      });
  }

  public goToAlarms(): void {
    console.log('go to alarms 1');
    this._router.navigate(['/alarms'])
      .then();
  }

  public goToReports(): void {
    let type: ReportType = ReportType.MEASUREMENT
    if (!this._chartFromDate || !this._chartToDate) return
    let from: number = Math.floor(this._chartFromDate.getTime() / 1000);
    let to: number = Math.floor(this._chartToDate.getTime() / 1000);

    this._router.navigate(['/reports'], { queryParams: { type, from, to } })
      .then();
  }

  public onDistrictSelected(district: string | null): void {
    this._selectedDistrict = district;

    if (this._selectedDistrict) {
      this.setMixedChartData(
        this._chartDistrictData[this._selectedDistrict] ?? [],
      );
    } else {
      this.setMixedChartData(this._chartData);
    }
  }

  public requestMeasurement(range: MixedChartDateRangeInterface): void {
    const now: Date = new Date();
    const fromDate: Date = this._diagramService.roundDateToBucketStart(range.from, range.type);
    const toDate: Date = range.to > now ? now : range.to;

    const fromNumber: number = Math.floor(fromDate.getTime() / 1000);
    const toNumber: number = Math.floor(toDate.getTime() / 1000);

    this.waterMeasurementData = {
      ...this.waterMeasurementData,
      loading: true,
    };

    this._deviceService.waterMeasurementData = this.waterMeasurementData;

    this._deviceService.waterMeasurementRange = {
      fromDate: fromNumber,
      toDate: toNumber,
    };

    this._deviceService.getDataMeasurements({
      measurement: 'METER_VALUE_1',
      fromDate: fromNumber,
      toDate: toNumber,
    })
      .subscribe({
        next: (result: MeasurementResponseDto) => {
          if (!result.data || result.data.length === 0) {
            this.clearWaterMeasurementData();

            return;
          }

          if (!this._selectedDistrict) {
            this.setMixedChartData(
              result.data,
              fromDate,
              toDate,
              range.type,
            );
          }

          this._chartData = result.data;
        },
        error: (error: unknown) => {
          this.waterMeasurementData = {
            ...this.waterMeasurementData,
            loading: false,
          };

          this._deviceService.waterMeasurementData = this.waterMeasurementData;

          console.error('Error fetching device measurements:', error);
        },
      });

    this._deviceService.getDistrictMeasurements({
      measurement: 'METER_VALUE_1',
      fromDate: fromNumber,
      toDate: toNumber,
    })
      .subscribe({
        next: (districtResult: DistrictMeasurementResponseDto) => {

          this.setTreeTableDate(
            districtResult.data,
            fromDate,
            toDate,
            range.type,
          );

          if (this._selectedDistrict) {
            this.setMixedChartData(
              districtResult.data[this._selectedDistrict],
              fromDate,
              toDate,
              range.type,
            );
          }

          this._chartDistrictData = districtResult.data;
        },
      });

    this._chartFromDate = fromDate;
    this._chartToDate = toDate;
    this._chartType = range.type;
  }

  private _getAlarmCountBySeverity(severity: AlarmSeverity): number {
    return this.alarms.filter((alarm: AlarmDto) =>
      this._isOpenAlarm(alarm)
      && alarm.severity === severity,
    ).length;
  }

  private _onlineDeviceList(): number {
    return this.devices.filter((device: DeviceDto) => device.status === 'online').length;
  }

  private setMixedChartData(
    data: DeviceMeasurementDto[],
    fromDate?: Date,
    toDate?: Date,
    type?: TimeRangeKey,
  ): void {
    fromDate ??= this._chartFromDate;
    toDate ??= this._chartToDate;
    type ??= this._chartType;

    if (!fromDate || !toDate || !type) return;

    const timeSeriesData: {
      labels: string[],
      data: number[]
    } = this._diagramService.generateTimeSeriesData(
      new Date(fromDate),
      new Date(toDate),
      type,
      data,
    );

    this.waterMeasurementData = {
      loading: false,
      labels: timeSeriesData.labels,
      datasets: [
        {
          ...this.waterMeasurementData.datasets[0],
          data: timeSeriesData.data,
        },
        {
          ...this.waterMeasurementData.datasets[1],
          data: this._diagramService.calcDeltaValues(timeSeriesData.data), // Delta
        },
      ],
    };

    this._deviceService.waterMeasurementData = this.waterMeasurementData;
  }

  private setTreeTableDate(
    data: DistrictMeasurementsDto,
    from?: Date,
    to?: Date,
    type?: TimeRangeKey,
  ): void {
    from ??= this._chartFromDate;
    to ??= this._chartToDate;
    type ??= this._chartType;
    if (!from || !to || !type) return;

    this.waterUsagePerDistrictData = [];

    for (let district in data) {
      const rawMeasurements: DistrictMeasurementItemDto[] = (data[district] ?? []);

      const timeSeriesData: {
        labels: string[],
        data: number[]
      } = this._diagramService.generateTimeSeriesData(
        new Date(from),
        new Date(to),
        type,
        rawMeasurements,
      );

      const deviceCounts: number[] = [];
      const delta: number[] = this._diagramService.calcDeltaValues(timeSeriesData.data);

      delta.reverse();
      timeSeriesData.labels.reverse();
      timeSeriesData.data.reverse();

      const children: TreeNode<WaterUsageTreeRow>[] = timeSeriesData.labels.map((label: string, index: number) => ({
        label,
        data: {
          district: label,
          waterUsage: timeSeriesData.data[index] ?? 0,
          delta: delta[index] ?? 0,
          type: 'Day',
          deviceCount: deviceCounts[index] ?? 0,
        },
      }));

      this.waterUsagePerDistrictData.push({
        label: district,
        data: {
          district,
          waterUsage: timeSeriesData.data[0] ?? 0,
          delta: delta[0] ?? 0,
          type: 'District',
          deviceCount: deviceCounts[0] ?? 0,
        },
        children,
      });
    }
  }

  private clearWaterMeasurementData(): void {
    this.waterMeasurementData = {
      ...this.waterMeasurementData,
      loading: false,
      labels: [],
      datasets: this.waterMeasurementData.datasets.map(dataset => ({
        ...dataset,
        data: [],
      })),
    };

    this._deviceService.waterMeasurementData = this.waterMeasurementData;
  }

  private _isOpenAlarm(alarm: AlarmDto): boolean {
    return alarm.status === AlarmStatus.ACTIVE
      || alarm.status === AlarmStatus.ACKNOWLEDGED;
  }

  protected readonly AccountRole = AccountRole;

  public hasAccess(role: AccountRole): boolean {
    return this._authService.canAccess(role);
  }
}
